import { FC, useId } from "react";

import { Stack, useTheme } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";

import { ThemedDonor, ThemedDonorProps } from "@/features/FormV2/sharedComponents/ThemedDonor";
import { ThemedDonorSkeleton } from "@/features/FormV2/sharedComponents/ThemedDonorSkeleton";
import { FormDialog, FormDialogProps } from "@/forms-design-system/FormDialog/FormDialog";
import { FormDivider } from "@/forms-design-system/FormDivider";
import { useTranslate } from "@/hooks/useTranslate";

type ThemedDonorsListDialogProps = Omit<FormDialogProps, "title" | "children" | "actions" | "noPadding"> & {
  isLoading?: boolean;
  donors: ThemedDonorProps[];
  hasMoreDonors: boolean;
  onFetchMoreDonors: () => void;
  now?: Date;
};

export const ThemedDonorsListDialog: FC<ThemedDonorsListDialogProps> = ({
  isLoading = false,
  donors,
  open,
  onClose,
  hasMoreDonors,
  onFetchMoreDonors,
  now,
  ...rest
}) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const renderInfiniteScrollLoader = () => {
    return (
      <Stack gap={2} marginBlockStart={2}>
        <FormDivider />
        <Stack gap={2} divider={<FormDivider />}>
          {Array.from({ length: 4 }).map((_, index) => (
            <ThemedDonorSkeleton key={index} />
          ))}
        </Stack>
      </Stack>
    );
  };

  const scrollableTargetId = useId();

  return (
    <FormDialog
      title={t("donationForm", "peerToPeer.donorsDialog.title")}
      open={open}
      onClose={onClose}
      noPadding
      slotProps={{
        content: {
          id: scrollableTargetId,
        },
      }}
      {...rest}
    >
      <InfiniteScroll
        dataLength={donors.length}
        next={onFetchMoreDonors}
        hasMore={hasMoreDonors}
        loader={renderInfiniteScrollLoader()}
        style={{ padding: theme.spacing(3) }}
        scrollableTarget={scrollableTargetId}
      >
        <Stack gap={2} divider={<FormDivider />}>
          {isLoading
            ? Array.from({ length: 7 }).map((_, index) => <ThemedDonorSkeleton key={index} />)
            : donors.map((donor, index) => <ThemedDonor key={index} {...donor} now={now} />)}
        </Stack>
      </InfiniteScroll>
    </FormDialog>
  );
};
